import "./Promotions.scss";

import { Badge, Button, Col, Row } from "react-bootstrap";
import {
    LeadingActions,
    SwipeAction,
    SwipeableList,
    SwipeableListItem,
    TrailingActions,
} from "react-swipeable-list";
import React, { useEffect, useMemo, useState } from "react";
import {
    faAdd,
    faArrowRight,
    faCalendar,
    faClock,
    faEdit,
    faEye,
    faInfoCircle,
    faStar,
    faTrash,
} from "@fortawesome/free-solid-svg-icons";

import AppLoading from "../../commons/AppLoading";
import ConfirmModal from "../../commons/modal/ConfirmModal";
import CustomCard from "../../commons/cards/Card";
import EmptyState from "../../commons/EmptyState";
import FloatingButton from "../../commons/buttons/FloatingButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HallAppCenterContainer from "../../commons/containers/HallAppCenterContainer";
import HallAppToast from "../../commons/HallAppToast";
import MenuTitle from "../../commons/menu/MenuTitle";
import ModalPromotion from "./components/ModalPromotion";
import PromotionAPI from "./PromotionAPI";
import PullToRefresh from "react-simple-pull-to-refresh";

const WEEK_LABEL = {
    SUNDAY: "Dom",
    MONDAY: "Seg",
    TUESDAY: "Ter",
    WEDNESDAY: "Qua",
    THURSDAY: "Qui",
    FRIDAY: "Sex",
    SATURDAY: "Sáb",
};

function formatHumanDate(iso) {
    if (!iso) return "—";
    const [y, m, d] = String(iso).split("-");
    if (!y || !m || !d) return iso;
    return `${d}/${m}/${y}`;
}

function formatMoney(v) {
    if (v == null || Number.isNaN(Number(v))) return "—";
    return Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function discountLabel(type, value) {
    if (!type) return "—";
    if (type === "PERCENT") return `${Number(value ?? 0)}% OFF`;
    if (type === "FIXED") return `${formatMoney(value)} OFF`;
    if (type === "FINAL_PRICE") return `Por ${formatMoney(value)}`;
    return String(type);
}

function statusVariant(active) {
    return active ? "success" : "secondary";
}

function visibilityVariant(showToClients) {
    return showToClients ? "primary" : "dark";
}

function highlightVariant(highlightAgenda) {
    return highlightAgenda ? "warning" : "light";
}

const promotionAPI = new PromotionAPI();

const Promotions = () => {
    const [showModalConfig, setShowModalConfig] = useState(false);

    const [isLoading, setIsLoading] = useState(true);

    const [promotions, setPromotions] = useState([]);
    const [totalResultRows, setTotalResultRows] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [pageInfo, setPageInfo] = useState({ number: 0, size: 12, numberOfElements: 0, first: true, last: false });

    const [isShowNotFoundPage, setShowNotfoundPage] = useState(false);

    const [filter, setFilter] = useState({
        page: 0,
        limit: 12,
        name: "",
    });

    // input controlado + debounce
    const [searchName, setSearchName] = useState("");

    const [showModalDelete, setShowModalDelete] = useState(false);
    const [isLoadingModalDelete, setIsLoadingModalDelete] = useState(false);
    const [selectedPromotion, setSelectedPromotion] = useState(null);

    useEffect(() => {
        if (filter.page === 0) {
            setPromotions([]);
            setTotalResultRows(0);
            setTotalPages(0);
            setPageInfo({ number: 0, size: filter.limit, numberOfElements: 0, first: true, last: false });
        }
        findAllPromotions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter.page, filter.limit, filter.name]);

    // debounce da busca
    useEffect(() => {
        const t = setTimeout(() => {
            setFilter((prev) => ({
                ...prev,
                name: String(searchName || "").trim(),
                page: 0, // reset paginação ao buscar
            }));
        }, 350);

        return () => clearTimeout(t);
    }, [searchName]);

    const findAllPromotions = async () => {
        try {
            setIsLoading(true);

            const resp = await promotionAPI.findAllPromotions(filter.page, filter.limit, filter.name);
            const data = resp?.data || {};

            // suporte tanto Page quanto array simples (fallback)
            const content = Array.isArray(data) ? data : (data.content || []);

            const total =
                typeof data.totalElements === "number"
                    ? data.totalElements
                    : (typeof data.totalResultRows === "number" ? data.totalResultRows : content.length);

            const tp =
                typeof data.totalPages === "number"
                    ? data.totalPages
                    : (total ? Math.ceil(total / (filter.limit || 1)) : 0);

            // infos úteis do pageable
            const info = {
                number: typeof data.number === "number" ? data.number : filter.page,
                size: typeof data.size === "number" ? data.size : filter.limit,
                numberOfElements: typeof data.numberOfElements === "number" ? data.numberOfElements : content.length,
                first: typeof data.first === "boolean" ? data.first : filter.page === 0,
                last: typeof data.last === "boolean" ? data.last : false,
            };

            setPromotions((prev) => [...prev, ...content]);
            setTotalResultRows(total);
            setTotalPages(tp);
            setPageInfo(info);

            const noItems = filter.page === 0 && (!content || content.length === 0);
            setShowNotfoundPage(noItems);
        } catch (e) {
            if (filter.page === 0) {
                setPromotions([]);
                setTotalResultRows(0);
                setTotalPages(0);
                setPageInfo({ number: 0, size: filter.limit, numberOfElements: 0, first: true, last: false });
                setShowNotfoundPage(true);
            }
            HallAppToast.genericError();
        } finally {
            setIsLoading(false);
        }
    };

    // ✅ preferir last/totalPages quando disponível (evita inconsistência)
    const hasMore = useMemo(() => {
        if (typeof totalPages === "number" && totalPages > 0) {
            return (pageInfo.number + 1) < totalPages;
        }
        return promotions.length < totalResultRows;
    }, [totalPages, pageInfo.number, promotions.length, totalResultRows]);

    const loadMore = () => {
        if (!hasMore) return;
        setFilter((prev) => ({ ...prev, page: prev.page + 1 }));
    };

    const deleteAction = (data) => (
        <TrailingActions>
            <SwipeAction
                destructive={false}
                onClick={() => {
                    setSelectedPromotion(data);
                    setShowModalDelete(true);
                }}
            >
                <div className="swipe">
                    <div className="swipe swipe-dlt">
                        <FontAwesomeIcon icon={faTrash} size="lg" />
                        <div>EXCLUIR</div>
                    </div>
                </div>
            </SwipeAction>
        </TrailingActions>
    );

    const editAction = (data) => (
        <LeadingActions>
            <SwipeAction
                onClick={() => {
                    setSelectedPromotion(data);
                    setShowModalConfig(true);
                }}
            >
                <div className="swipe">
                    <div className="swipe swipe-edt">
                        <FontAwesomeIcon icon={faEdit} size="lg" />
                        <div>EDITAR</div>
                    </div>
                </div>
            </SwipeAction>
        </LeadingActions>
    );

    async function executeDeletion() {
        setIsLoadingModalDelete(true);
        try {
            await promotionAPI.deletePromotion(selectedPromotion.id);
            setShowModalDelete(false);
            setSelectedPromotion(null);

            // reset e recarrega do zero pra manter contagem/paginação consistente            
            HallAppToast.success("Promoção removida.");
            refreshPromotions();
        } catch (_) {
            HallAppToast.genericError();
        } finally {
            setIsLoadingModalDelete(false);
        }
    }

    const subtitleFor = (p) => {
        const dateRange = p.noEndDate
            ? `${formatHumanDate(p.startDate)} • Sem data final`
            : `${formatHumanDate(p.startDate)} até ${formatHumanDate(p.endDate)}`;

        const hourRange =
            p.startHour || p.endHour ? `${p.startHour || "—"} - ${p.endHour || "—"}` : "Sem horário";

        const uses = p.limitTotalUses != null ? `Limite: ${p.limitTotalUses}` : "Sem limite";

        return { dateRange, hourRange, uses };
    };

    const weeksChips = (validWeeks) => {
        const arr = Array.isArray(validWeeks) ? validWeeks : [];
        if (!arr.length) return null;
        return (
            <div className="d-flex flex-wrap gap-2 mt-2">
                {arr.map((w) => (
                    <span
                        key={w}
                        className="px-2 py-1 rounded-pill"
                        style={{
                            fontSize: 12,
                            background: "#f1f3ff",
                            color: "#2f2a7a",
                            border: "1px solid #e3e6ff",
                            fontWeight: 800,
                        }}
                        title={w}
                    >
                        {WEEK_LABEL[w] || w}
                    </span>
                ))}
            </div>
        );
    };

    const items = useMemo(() => {
        // mantém ordenação do backend (provavelmente DESC id) — mas se quiser manter por startDate:
        return [...promotions];
    }, [promotions]);

    const toggleActive = async (promotion) => {
        try {
            setPromotions((prev) => prev.map((p) => (p.id === promotion.id ? { ...p, active: !p.active } : p)));

            await promotionAPI.toggleActive(promotion.id, !promotion.active);

            // recarrega página 0 (mantendo busca atual)
            setFilter((prev) => ({ ...prev, page: 0 }));

            HallAppToast.success(promotion.active ? "Promoção desativada." : "Promoção ativada.");
        } catch (e) {
            HallAppToast.genericError();
            setPromotions((prev) => prev.map((p) => (p.id === promotion.id ? { ...p, active: promotion.active } : p)));
        }
    };

    const refreshPromotions = async () => {
        // reset local imediato
        setPromotions([]);
        setTotalResultRows(0);
        setTotalPages(0);
        setShowNotfoundPage(false);

        // volta paginação pro início, mas mantém busca
        setFilter((prev) => ({ ...prev, page: 0 }));

        // PullToRefresh precisa de Promise
        await findAllPromotions();
    };

    // ✅ texto de contagem: totalElements vindo do Page
    const totalLabel = useMemo(() => {
        const total = Number(totalResultRows || 0);
        if (total === 0) return "0 promoções";
        if (total === 1) return "1 promoção";
        return `${total} promoções`;
    }, [totalResultRows]);

    // ✅ status do “mostrando X de Y”
    const showingLabel = useMemo(() => {
        const shown = promotions.length;
        const total = Number(totalResultRows || 0);
        if (!total) return "";
        return `Mostrando ${shown} de ${total}`;
    }, [promotions.length, totalResultRows]);

    const pageLabel = useMemo(() => {
        if (!totalPages) return "";
        return `Página ${pageInfo.number + 1} de ${totalPages}`;
    }, [pageInfo.number, totalPages]);

    return (
        <>
            {isLoading && filter.page === 0 ? (
                <HallAppCenterContainer>
                    <AppLoading />
                </HallAppCenterContainer>
            ) : (
                <>
                    <PullToRefresh onRefresh={refreshPromotions}>
                        <div className="container">
                            <MenuTitle title="Promoções" showArrowInfo={false} />

                            {/* ✅ topo: busca + contadores */}
                            <div style={{ marginTop: 10 }}>
                                <div className="d-flex align-items-center justify-content-between mt-2">
                                    <div className="text-muted" style={{ fontSize: 12, fontWeight: 800 }}>
                                        {totalLabel}
                                        {showingLabel ? <span style={{ fontWeight: 600 }}> • {showingLabel}</span> : null}
                                    </div>

                                    <div className="text-muted" style={{ fontSize: 12, fontWeight: 800 }}>
                                        {pageLabel}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {isShowNotFoundPage ? (
                            <div style={{ marginTop: "120px" }}>
                                <EmptyState
                                    title="Nenhuma promoção cadastrada"
                                    description="Crie promoções por porcentagem, desconto em reais ou preço final. Escolha os serviços, profissionais e dias válidos."
                                    icon={<FontAwesomeIcon icon={faCalendar} />}
                                    actionLabel="Criar promoção"
                                    onAction={() => setShowModalConfig(true)}
                                />
                            </div>
                        ) : (
                            <div className="container" style={{ paddingBottom: "120px" }}>
                                <Row className="g-3" style={{ margin: 0, width: "100%", paddingTop: 10 }}>
                                    {items.map((p) => {
                                        const { dateRange, hourRange, uses } = subtitleFor(p);

                                        return (
                                            <Col xs={12} sm={6} md={4} lg={4} key={p.id} className="mb-3">
                                                <SwipeableList style={{ width: "100%" }}>
                                                    <SwipeableListItem
                                                        leadingActions={editAction(p)}
                                                        trailingActions={deleteAction(p)}
                                                        key={p.id}
                                                        fullSwipe={false}
                                                    >
                                                        <div style={{ width: "100%" }}>
                                                            <CustomCard>
                                                                {/* Header */}
                                                                <div className="d-flex align-items-start justify-content-between gap-2">
                                                                    <div style={{ minWidth: 0 }}>
                                                                        <div className="fw-semibold" style={{ fontSize: 16, lineHeight: "20px" }}>
                                                                            {p.name}
                                                                        </div>
                                                                        <div className="text-muted" style={{ fontSize: 12 }}>
                                                                            {p.description || "—"}
                                                                        </div>
                                                                    </div>

                                                                    <div className="d-flex flex-column align-items-end gap-2">
                                                                        <Badge bg={statusVariant(p.active)} pill>
                                                                            {p.active ? "Ativa" : "Inativa"}
                                                                        </Badge>

                                                                        <button
                                                                            type="button"
                                                                            onClick={() => toggleActive(p)}
                                                                            style={{
                                                                                borderRadius: 10,
                                                                                border: "1px solid",
                                                                                borderColor: p.active ? "#dc3545" : "#198754",
                                                                                background: "#fff",
                                                                                color: p.active ? "#dc3545" : "#198754",
                                                                                padding: "4px 10px",
                                                                                fontSize: 11,
                                                                                fontWeight: 800,
                                                                                cursor: "pointer",
                                                                                whiteSpace: "nowrap",
                                                                            }}
                                                                        >
                                                                            {p.active ? "Desativar" : "Ativar"}
                                                                        </button>
                                                                    </div>
                                                                </div>

                                                                <hr className="my-3" />

                                                                {/* Badges complementares */}
                                                                <div className="d-flex flex-wrap gap-2 mb-3">
                                                                    <Badge bg={visibilityVariant(p.showToClients)} pill>
                                                                        <FontAwesomeIcon icon={faEye} className="me-1" />
                                                                        {p.showToClients ? "Visível" : "Oculta"}
                                                                    </Badge>

                                                                    <Badge bg={highlightVariant(p.highlightAgenda)} pill>
                                                                        <FontAwesomeIcon icon={faStar} className="me-1" />
                                                                        {p.highlightAgenda ? "Destaque" : "Sem destaque"}
                                                                    </Badge>

                                                                    <Badge bg="light" text="dark" pill style={{ fontWeight: 900 }}>
                                                                        {discountLabel(p.discountType, p.discountValue)}
                                                                    </Badge>
                                                                </div>

                                                                {/* Infos principais */}
                                                                <div className="d-flex flex-column gap-2">
                                                                    <div className="d-flex align-items-center justify-content-between">
                                                                        <div className="text-muted d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
                                                                            <FontAwesomeIcon icon={faCalendar} />
                                                                            <span>Período</span>
                                                                        </div>
                                                                        <div className="fw-semibold" style={{ fontSize: 13, textAlign: "right" }}>
                                                                            {dateRange}
                                                                        </div>
                                                                    </div>

                                                                    <div className="d-flex align-items-center justify-content-between">
                                                                        <div className="text-muted d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
                                                                            <FontAwesomeIcon icon={faClock} />
                                                                            <span>Horário</span>
                                                                        </div>
                                                                        <div className="fw-semibold" style={{ fontSize: 13 }}>
                                                                            {hourRange}
                                                                        </div>
                                                                    </div>

                                                                    <div className="d-flex align-items-center justify-content-between">
                                                                        <div className="text-muted d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
                                                                            <span style={{ fontWeight: 900 }}>#</span>
                                                                            <span>Serviços</span>
                                                                        </div>
                                                                        <div className="fw-semibold" style={{ fontSize: 13 }}>
                                                                            {p.servicesCount != null ? p.servicesCount : "—"}
                                                                        </div>
                                                                    </div>

                                                                    <div className="d-flex align-items-center justify-content-between">
                                                                        <div className="text-muted d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
                                                                            <span style={{ fontWeight: 900 }}>∞</span>
                                                                            <span>Uso</span>
                                                                        </div>
                                                                        <div className="fw-semibold" style={{ fontSize: 13 }}>
                                                                            {uses}
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Dias válidos */}
                                                                {weeksChips(p.validWeeks)}

                                                                {/* ===================== MÉTRICAS ===================== */}
                                                                <div className="mt-3">
                                                                    <div className="text-muted mb-2" style={{ fontSize: 12, fontWeight: 800 }}>
                                                                        Métricas (últimos 30 dias)
                                                                    </div>

                                                                    <div className="d-flex flex-wrap gap-2">
                                                                        <span
                                                                            className="badge rounded-pill"
                                                                            style={{
                                                                                background: "#f8f9fa",
                                                                                color: "#111",
                                                                                border: "1px solid rgba(0,0,0,.08)",
                                                                                fontWeight: 900,
                                                                                padding: "8px 10px",
                                                                            }}
                                                                            title="Visualizações da promoção"
                                                                        >
                                                                            <FontAwesomeIcon icon={faEye} className="me-2" />
                                                                            {Number(p.views ?? 0)} views
                                                                            {p.uniqueViews != null ? (
                                                                                <span style={{ opacity: 0.7, fontWeight: 800, marginLeft: 6 }}>
                                                                                    ({Number(p.uniqueViews)} únicos)
                                                                                </span>
                                                                            ) : null}
                                                                        </span>

                                                                        <span
                                                                            className="badge rounded-pill"
                                                                            style={{
                                                                                background: "#f8f9fa",
                                                                                color: "#111",
                                                                                border: "1px solid rgba(0,0,0,.08)",
                                                                                fontWeight: 900,
                                                                                padding: "8px 10px",
                                                                            }}
                                                                            title="Cliques em detalhes"
                                                                        >
                                                                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                                                            {Number(p.clickDetails ?? 0)} detalhes
                                                                        </span>

                                                                        <span
                                                                            className="badge rounded-pill"
                                                                            style={{
                                                                                background: "#f8f9fa",
                                                                                color: "#111",
                                                                                border: "1px solid rgba(0,0,0,.08)",
                                                                                fontWeight: 900,
                                                                                padding: "8px 10px",
                                                                            }}
                                                                            title="Cliques no botão Agendar com desconto"
                                                                        >
                                                                            <FontAwesomeIcon icon={faArrowRight} className="me-2" />
                                                                            {Number(p.clickSchedule ?? 0)} agendar
                                                                            {p.uniqueClickSchedule != null ? (
                                                                                <span style={{ opacity: 0.7, fontWeight: 800, marginLeft: 6 }}>
                                                                                    ({Number(p.uniqueClickSchedule)} únicos)
                                                                                </span>
                                                                            ) : null}
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                {/* Hint swipe */}
                                                                <div className="text-muted mt-3" style={{ fontSize: 11 }}>
                                                                    Dica: deslize para a esquerda para excluir
                                                                </div>
                                                            </CustomCard>
                                                        </div>

                                                    </SwipeableListItem>
                                                </SwipeableList>
                                            </Col>
                                        );
                                    })}
                                </Row>

                                <div className="feedback-loadmore" style={{ paddingBottom: 10 }}>
                                    {hasMore ? (
                                        <Button variant="outline-secondary" onClick={loadMore} disabled={isLoading}>
                                            {isLoading ? "Carregando..." : "Carregar mais"}
                                        </Button>
                                    ) : (
                                        <div className="feedback-end">Você já viu tudo</div>
                                    )}
                                </div>
                            </div>
                        )}
                    </PullToRefresh>
                </>
            )}

            <FloatingButton icon={faAdd} onClick={() => setShowModalConfig(true)} />

            {showModalDelete && (
                <ConfirmModal
                    show={showModalDelete}
                    title="Excluir"
                    isLoading={isLoadingModalDelete}
                    confirm={executeDeletion}
                    closeModal={() => {
                        setShowModalDelete(false);
                        setSelectedPromotion(null);
                    }}
                >
                    Deseja remover a promoção <span style={{ fontWeight: "bold" }}>{selectedPromotion?.name}</span>?
                </ConfirmModal>
            )}

            {showModalConfig && (
                <ModalPromotion
                    onSaved={() => {
                        setSelectedPromotion(null);
                        refreshPromotions()
                    }}
                    promotionId={selectedPromotion?.id}
                    show={showModalConfig}
                    closeShow={() => setShowModalConfig(false)}
                />
            )}
        </>
    );
};

export default Promotions;
